import { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import ProfilesActions from 'store/ducks/profiles';

/**
 * Hook para verificar permissões do usuário
 */
export function usePermissions() {
  const dispatch = useDispatch();
  const { permissions } = useSelector((state) => state.profiles);
  const userActive = useSelector((state) => state.auth.userActive);
  const [loading, setLoading] = useState(true);

  // SEMPRE usar apenas profile_id para buscar permissões (não usar mais role ou profile)
  // Removida verificação de isAdmin baseada em role/profile
  
  // logs de debug removidos

  useEffect(() => {
    // Carregar permissões se não estiverem carregadas
    // Verificar se permissions existe e se tem as propriedades definidas (mesmo que vazias)
    const hasPermissionsLoaded = permissions && (
      Array.isArray(permissions.modules) &&
      Array.isArray(permissions.companies) &&
      Array.isArray(permissions.registrations)
    );
    
    if (!hasPermissionsLoaded) {
      // Se não tem permissões carregadas, buscar
      if (userActive && userActive.profile_id) {
        setLoading(true);
        dispatch(ProfilesActions.getMyPermissionsRequest());
      } else {
        setLoading(false);
      }
    } else {
      // Permissões já carregadas (mesmo que vazias)
      setLoading(false);
    }
  }, [dispatch, permissions, userActive]);

  /**
   * Verifica se o usuário tem acesso ao módulo
   */
  const hasModuleAccess = (moduleId) => {
    // SEMPRE verificar permissões do profile_id (não usar mais isAdmin)
    if (!permissions || !permissions.modules) return false;
    return permissions.modules.includes(moduleId);
  };

  /**
   * Verifica se o usuário tem acesso à empresa
   */
  const hasCompanyAccess = (companyId) => {
    if (!permissions || !permissions.companies) return true; // Se não tiver empresas configuradas, permitir todas
    if (permissions.companies.length === 0) return true; // Se lista vazia, permitir todas
    return permissions.companies.includes(companyId);
  };

  /**
   * Verifica se o usuário tem permissão para uma operação em um tipo de cadastro
   */
  const hasPermission = (registrationType, operation) => {
    // SEMPRE verificar permissões do profile_id (não usar mais isAdmin)
    if (!permissions || !permissions.registrations) return false;

    const registration = permissions.registrations.find(
      (r) => r.registration_type === registrationType
    );

    if (!registration) return false;

    switch (operation) {
      case 'view':
      case 'read':
        return registration.can_view !== false; // view sempre permitido por padrão
      case 'create':
        return registration.can_create === true;
      case 'update':
      case 'edit':
        return registration.can_update === true;
      case 'delete':
        return registration.can_delete === true;
      default:
        return false;
    }
  };

  /**
   * Verifica se pode visualizar
   */
  const canView = (registrationType) => {
    // SEMPRE verificar permissões do profile_id (não usar mais isAdmin)
    // Se não tem permissões carregadas, negar por segurança
    if (loading || !permissions || !permissions.registrations) return false;
    
    const registration = permissions.registrations.find(
      (r) => r.registration_type === registrationType
    );

    // Se não encontrou o tipo de cadastro nas permissões, negar acesso
    if (!registration) return false;
    
    // Verificar se tem permissão de visualização
    const hasView = registration.can_view !== false; // view sempre permitido por padrão se não for explicitamente false
    
    return hasView;
  };

  /**
   * Verifica se pode criar
   */
  const canCreate = (registrationType) => {
    return hasPermission(registrationType, 'create');
  };

  /**
   * Verifica se pode atualizar
   */
  const canUpdate = (registrationType) => {
    return hasPermission(registrationType, 'update');
  };

  /**
   * Verifica se pode deletar
   */
  const canDelete = (registrationType) => {
    return hasPermission(registrationType, 'delete');
  };

  return {
    permissions,
    loading,
    hasModuleAccess,
    hasCompanyAccess,
    hasPermission,
    canView,
    canCreate,
    canUpdate,
    canDelete,
  };
}

// Export default também para garantir compatibilidade
export default usePermissions;

